"use client";

import Image from "next/image";
import ImageWrapper from "@/component/ui/ImageWrapper";
import ShareButton from "@/component/ShareButton";
import BodyContent from "@/component/ui/BodyContent/BodyContent";
import CustomSlider from "@/component/ui/CustomSlider/CustomSlider";
import { MEDIA_TABS, SLIDER_CONFIG } from "@/app/[brand]/[model]/revampPageStaticData";
import { useEffect, useMemo, useRef, useState } from "react";
import { useImagePageMedia } from "@/component/pdp/image-page/ImagePageMedia";
import type { ImageGalleryItem } from "@/component/pdp/image-page/types";
import { hasMinimumImagePageGallery } from "@/component/pdp/image-page/galleryData";
import dynamic from "next/dynamic";
import EmiStartCard from "../EmiStartCard";
import { useEmiData } from "@/hooks/useEmiData";
import { useRTOPricingData } from "@/hooks/useRTOPricingData";
import { PDP_EMICALCULATOR_POPUP_TRACKING } from "@/lib/constants/tracking";
import Link from "next/link";
import LeadButton from "@/component/ui/Lead/LeadButton";
import { dateFormat, formatPriceRange } from "@/app/utilities/helper";
import { getLocationCookie } from "@/app/utilities/locationCookie";
import Heading from "@/component/ui/Heading";

const EmiCalculator = dynamic(() => import("@/component/ui/EmiCalculator/EmiCalculator"));

interface CarOverviewCardProps {
    data?: any;
    isMobile?: boolean;
    prefetchedCity?: string;
    prefetchedCitySlug?: string;
    cdnUrl?: string;
    // EMI-related prefetched values from server
    prefetchedRtoResult?: any;
    prefetchedInsuranceResult?: any;
    prefetchedVariantId?: string | number;
    EmiStartCardTracking?: string;
    isPriceInCityRoute?: boolean;
    variants?: any[];
    GORPFormHeading?: React.ReactNode;
    leadCTAText?: string;
    useSplashPopup?: boolean;
    gorpTrackingClass?: string;
    /** When true, description is shown in the page top section — skip duplicate in this card */
    suppressDescription?: boolean;
    description?: string;
    latestModel?: any;
    /** Gallery API images (with fallback from buildImagePageGalleryData). */
    heroGalleryImages?: ImageGalleryItem[];
    hasColours?: boolean;
}

export default function CarOverviewCard({
    data,
    isMobile = false,
    prefetchedCity = "",
    prefetchedCitySlug = "",
    cdnUrl = "",
    prefetchedRtoResult,
    prefetchedInsuranceResult,
    prefetchedVariantId,
    EmiStartCardTracking,
    isPriceInCityRoute = false,
    variants = [],
    GORPFormHeading,
    leadCTAText,
    useSplashPopup,
    gorpTrackingClass,
    suppressDescription = false,
    description: descriptionProp = "",
    latestModel,
    heroGalleryImages,
    hasColours = false,
}: CarOverviewCardProps) {
    const [activeSlideIndex, setActiveSlideIndex] = useState(0);
    const { openLightbox } = useImagePageMedia();

    const offerBtnRef = useRef<HTMLDivElement>(null);
    const [showStickyBtn, setShowStickyBtn] = useState(false);
    const [citySlug, setCitySlug] = useState(prefetchedCitySlug);

    // Pricing & variant state (mirrors ProductHeroSection logic).
    // data?.base_variant is never populated by /model/detail — derive from
    // the variants list prop instead.
    const baseVariant = variants.find((v: any) => v.is_base_variant) ?? variants[0] ?? null;
    const [selectedVariant, setSelectedVariant] = useState<any>(baseVariant);
    const price = data?.price_range?.base || selectedVariant?.price || baseVariant?.price || 0;

    // Prepare car data from props
    const carName = data?.brand_name && data?.name ? `${data.brand_name} ${data.name}` : "Car Name";

    const legacyImages = data?.images && data.images.length > 0 ? data.images : [];
    const allHeroSlides = useMemo(() => {
        if (heroGalleryImages?.length) {
            return heroGalleryImages.map((image) => ({
                src: image.src,
                alt: image.alt,
                galleryId: image.id,
            }));
        }
        return legacyImages.map((img: any, idx: number) => ({
            src: cdnUrl + (img?.url || img),
            alt: `${data?.brand_name ?? ""} ${data?.name ?? ""} Image ${idx + 1}`.trim(),
            galleryId: idx,
        }));
    }, [heroGalleryImages, legacyImages, cdnUrl, data?.brand_name, data?.name]);

    const carouselSlides = allHeroSlides;
    const totalImageCount = allHeroSlides.length;
    const images = carouselSlides;
    const isDiscontinued = data?.status === 'discontinued';
    const isUpcoming = data?.status === 'upcoming';
    const description = descriptionProp;
    const isPriceAvailable = !!price;
    const variantCount = variants?.length || Number(data?.variant_count) || 0;

    // custom hook to get rto and insurance pricing data for selected variant and city
    const { rtoResult, insuranceResult, isRtoLoading, isInsuranceLoading, selectedCity } = useRTOPricingData({
        model: data,
        selectedVariant,
        price,
        isPriceInCityRoute,
        prefetchedRtoResult,
        prefetchedInsuranceResult,
        prefetchedVariantId,
        prefetchedCity,
    });

    // custom hook to get EMI data for selected variant and city
    const { emiData, isEmiOpen, emiSelectedVariant, emiOnRoadPrices, handleOpenEmiCalculator, setIsEmiOpen, setEmiSelectedVariant } = useEmiData({
        model: data,
        selectedVariant,
        variants,
        selectedCity,
        isPriceInCityRoute,
        rtoResult,
        insuranceResult,
        isRtoLoading,
        isInsuranceLoading,
        price,
    });

    // Sticky button on scroll (mobile only)
    useEffect(() => {
        const handleScroll = () => {
            if (window.innerWidth >= 768) return;
            const button = offerBtnRef.current;
            if (!button) return;
            const rect = button.getBoundingClientRect();
            setShowStickyBtn(rect.bottom < 120);
        };
        window.addEventListener("scroll", handleScroll);
        handleScroll();
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    // Keep the on-road price link in sync with the city picked in the location modal
    useEffect(() => {
        const cookieSlug = getLocationCookie()?.slug;
        if (cookieSlug) setCitySlug(cookieSlug);

        const handleLocationUpdate = (event: Event) => {
            const slug = (event as CustomEvent)?.detail?.location?.slug;
            if (slug) setCitySlug(slug);
        };

        window.addEventListener("location-updated", handleLocationUpdate);
        return () => window.removeEventListener("location-updated", handleLocationUpdate);
    }, []);

    const handleOpenLocation = () => {
        window.dispatchEvent(new CustomEvent("open-location-modal"));
    };

    const scrollToSection = (id: string) => {
        const element = document.getElementById(id);
        if (!element) return;

        const headerHeight = document.querySelector("header")?.getBoundingClientRect().height ?? 65;
        const secondaryNavHeight = document.querySelector("[data-secondary-nav]")?.getBoundingClientRect().height ?? 0;
        const top = element.getBoundingClientRect().top + window.scrollY - (headerHeight + secondaryNavHeight) + 1;

        window.scrollTo({ top, behavior: "smooth" });
    };

    // Price formatting
    const priceRange = formatPriceRange(data?.price_range);
    const brandSlug = data?.brand_slug ?? data?.brand?.slug ?? "";

    const sliderConfig = isMobile ? SLIDER_CONFIG.mobile : SLIDER_CONFIG.desktop;

    const carouselSlideCount = carouselSlides.length;
    const counterTotal = totalImageCount || carouselSlideCount;
    const totalImages =
        carouselSlideCount > 0
            ? `${activeSlideIndex + 1} / ${counterTotal}`
            : "";

    const mediaTabs = MEDIA_TABS.filter((tab) => tab.id !== "colours" || hasColours);

    return (
        <div className="w-full overflow-hidden rounded-[0px] md:rounded-[24px] border-0 md:border md:border-gray-200 bg-white">
            <div className="flex flex-col lg:flex-row">
                {/* Left Section - Image Slider */}
                <div className="w-full lg:w-[42%]">
                    <div className="relative product-slider w-full rounded-l">
                        {isDiscontinued && (
                            <span className="absolute top-2 left-2 z-10 px-2 py-1 ">
                                <Image src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}discontinued-icon.svg`} alt="" width={82} height={82} />
                            </span>
                        )}
                        {images && images.length > 0 ? (
                            <CustomSlider
                                itemsToShow={sliderConfig.itemsToShow}
                                showArrows={sliderConfig.showArrows}
                                showDots={isMobile ? sliderConfig.showDots : false}
                                autoSlide={sliderConfig.autoSlide}
                                autoSlideInterval={sliderConfig.autoSlideInterval}
                                infinite={sliderConfig.infinite}
                                // onSlideChange={(index) => setCurrentImageIndex(index)}
                                onSlideChange={(index) => {
                                    if (!carouselSlideCount) return;
                                    const nextIndex = Math.min(
                                        Math.max(0, index),
                                        carouselSlideCount - 1,
                                    );
                                    setActiveSlideIndex(nextIndex);
                                }}
                                className="car-overview-slider"
                                variant="boxed-icon"
                            >
                                {images.map((img: { src: string; alt: string; galleryId: string | number }, idx: number) => (
                                    <button
                                        key={idx}
                                        type="button"
                                        aria-label={img.alt}
                                        onClick={() => openLightbox(img.galleryId, "all", "all")}
                                        className="relative w-full aspect-529/350 cursor-pointer focus:outline-none"
                                    >
                                        <ImageWrapper
                                            src={img.src}
                                            alt={img.alt}
                                            fill
                                            sizes="(max-width: 1023px) 100vw, 42vw"
                                            sourceWidth={isMobile ? 418 : 732}
                                            sourceHeight={isMobile ? 235 : 411}
                                            fetchPriority={idx === 0 ? "high" : "low"}
                                            className="rounded-br-[0px] md:rounded-br-[24px] object-contain"
                                            title={img.alt}
                                            priority={idx === 0}
                                        />
                                    </button>
                                ))}
                            </CustomSlider>
                        ) : (
                            <ImageWrapper src="/images/placeholder-car.svg" alt={carName} width={529} height={326} className="w-full rounded-br-2xl object-cover" priority />
                        )}

                        {/* Image Counter - Desktop Only */}
                        {!isMobile && carouselSlideCount > 0 && totalImages && (
                            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-white px-3 py-1 text-xs shadow z-10">{totalImages}</div>
                        )}

                       {/* {isMobile && totalSlides > 0 && (
                            <div className="flex items-center justify-center gap-[6px] mt-3 min-h-[12px] absolute bottom-[10px] left-1/2 -translate-x-1/2 bg-black px-2 py-1 rounded-[20px]">
                                {visibleDots.map((dotIdx) => {
                                    const isActive = dotIdx === activeSlideIndex;
                                    const distance = Math.abs(dotIdx - activeSlideIndex);

                                    let sizeStyles = "w-[4px] h-[4px]";

                                    if (isActive) {
                                        sizeStyles = "w-[24px] h-[8px]";
                                    } else if (distance === 1) {
                                        sizeStyles = "w-[8px] h-[8px]";
                                    } else if (distance === 2) {
                                        sizeStyles = "w-[6px] h-[6px]";
                                    }

                                    return (
                                        <button
                                            key={`dot-${dotIdx}`}
                                            type="button"
                                            onClick={() => setActiveSlideIndex(dotIdx)}
                                            aria-label={`Go to slide ${dotIdx + 1}`}
                                            className={`rounded-full transition-all duration-300 ease-in-out cursor-pointer ${
                                                isActive
                                                    ? "bg-[#008C8B]"
                                                    : "bg-[#D9D9D9]"
                                            } ${sizeStyles}`}
                                        />
                                    );
                                })}
                            </div>
                        )} */}
                    </div>

                    {/* Media Tabs */}
                    {mediaTabs.length > 0 && (
                    <div className="flex flex-wrap justify-left gap-2 gap-md-3 p-3">
                        {mediaTabs.map((tab) => {
                            const tabClassName = "flex items-center gap-2 gap-md-3 rounded-full border border-gray-200 px-4 py-2 text-sm transition hover:bg-gray-50 cursor-pointer";
                            const tabContent = (
                                <>
                                    <Image src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}${tab.icon}`} alt={tab.label} width={20} height={20} className="size-5" />
                                    <span>{tab.label}</span>
                                </>
                            );

                            if (tab.href) {
                                if (!hasMinimumImagePageGallery(totalImageCount)) return null;
                                return (
                                    <button
                                        key={tab.label}
                                        type="button"
                                        onClick={() => {
                                            const firstSlide = images[0];
                                            openLightbox(
                                                firstSlide?.galleryId ?? heroGalleryImages?.[0]?.id,
                                                "all",
                                                "all",
                                            );
                                        }}
                                        className={tabClassName}
                                    >
                                        {tabContent}
                                    </button>
                                );
                            }

                            if (tab.id === "colours") {
                                return (
                                    <button
                                        key={tab.label}
                                        type="button"
                                        onClick={() => openLightbox(undefined, "colour", "all")}
                                        className={tabClassName}
                                    >
                                        {tabContent}
                                    </button>
                                );
                            }

                            return (
                                <button
                                    key={tab.label}
                                    type="button"
                                    onClick={() => {
                                        if (!tab.id) return;
                                        scrollToSection(tab.id);
                                    }}
                                    className={tabClassName}
                                >
                                    {tabContent}
                                </button>
                            );
                        })}
                    </div>
                    )}
                </div>

                {/* Right Section - Car Details */}
                <div className="flex-1 p-4 md:p-6" data-testid="pdp-hero-section">
                    <div className="flex items-center justify-between gap-4">
                        <Heading className="font-roboto text-[20px] leading-[28px] tracking-[0px] text-[#101828] md:text-[23px]   font-bold mb-3 !mb-0" tag="h2">{`${carName} Price`}</Heading>
                        <ShareButton />
                    </div>

                    {isUpcoming && (
                        <p className="text-[14px] text-[#4D5868] mt-2">
                            Expected Date of Launch :{" "}
                            <span className="text-[13px] md:text-[14px] font-[500] leading-[18px] text-[#008C8B]">
                                {data?.launch_date ? dateFormat(data?.launch_date) : "Date to be Announced"}
                            </span>{" "}
                        </p>
                    )}

                    {/* Description — mobile only; desktop renders this above CarOverviewCard */}
                    {description && !suppressDescription && (
                        <div className="mt-2 md:hidden">
                            <BodyContent fullText={description} containerClassName="mb-4" />
                        </div>
                    )}                    

                    {/* Price Location */}
                    <div className="flex flex-wrap items-center gap-2 text-sm mt-2">
                        <span className="text-gray-500">{isUpcoming ? "Estimated Price On Launch" : "Ex-showroom price in"}</span>
                        {isPriceAvailable && (
                            <button
                                type="button"
                                className="flex items-center gap-2 rounded-md border border-[#D8DEE8] bg-[#F8FAFC] px-3 py-1 bg text-[13px] font-medium leading-[15.6px] text-center cursor-pointer"
                                onClick={handleOpenLocation}
                            >
                                {selectedCity || prefetchedCity}
                                <Image
                                    src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}right-arrow-icon.svg`}
                                    alt="Chevron Right"
                                    title="Chevron Right"
                                    width={6}
                                    height={12}
                                    className="[filter:brightness(0)_saturate(100%)_invert(43%)_sepia(10%)_saturate(672%)_hue-rotate(169deg)_brightness(92%)_contrast(88%)]"
                                />
                            </button>
                        )}
                    </div>

                    {/* Price */}
                    <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2">
                        <p className="text-[18px] font-bold leading-[100%] tracking-[0px]">
                            {priceRange}
                            {priceRange !== "Price to be Announced" ? "*" : ""}
                        </p>

                        {!isDiscontinued && isPriceAvailable && (
                            <Link
                                prefetch={false}
                                href={`/${brandSlug}/${data?.slug}/price-in-${citySlug || ""}`}
                                title={`${data?.brand_name} ${data?.name} On-Road Price in ${selectedCity || prefetchedCity}`}
                                className="text-[14px] font-medium leading-[100%] text-[#155DFC] hover:underline"
                                data-testid="view-on-road-price-button"
                            >
                                View On Road Price
                            </Link>
                        )}
                    </div>

                    {/* Variants */}
                    {variantCount > 0 && (
                        <button
                            type="button"
                            onClick={() => scrollToSection("variants")}
                            className="mt-2 flex items-center gap-1 text-[13px] leading-[100%] text-[#5F6B7A] cursor-pointer"
                            data-testid="variants-button"
                        >
                            Across
                            <span className="text-[#155DFC] text-[13px] font-medium leading-[100%] underline">{variantCount}  {variantCount === 1 ? "variant" : "variants"}</span>
                            <svg width="10" height="6" viewBox="0 0 10 6" fill="none" aria-hidden="true">
                                <path d="M1 1L5 5L9 1" stroke="#5F6B7A" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                    )}

                    {/* EMI */}
                    {isDiscontinued ? (
                        <p className="mt-4 md:mt-6 flex-1 p-3 text-[14px] border border-red-500 text-red-500 bg-red-50 rounded-lg text-left">This model has been discontinued.</p>
                    ) : (
                        <div className="mt-4 w-full flex flex-col gap-4 md:w-[400px]">
                            {/* Render as soon as emiData exists — don't hide/reshow while RTO/insurance
                                resolve async, the amount updates in place via useMemo (avoids CLS). */}
                            {emiData && (
                                <EmiStartCard
                                    onClick={handleOpenEmiCalculator}
                                    emiAmount={emiData.amount}
                                    tooltipText={emiData.tooltip}
                                    TrackingEMI={EmiStartCardTracking}
                                    brand={data?.brand_name}
                                    model={data?.name}
                                    version="v2"
                                    dataTestId="calculate-emi-button"
                                    rightAction={
                                        !isUpcoming && (
                                            <CommonLeadButton
                                                GORPFormHeading={GORPFormHeading}
                                                isPriceInCityRoute={isPriceInCityRoute}
                                                leadCTAText="View EMI Offers"
                                                title={`${carName} View EMI Offers`}
                                                data={data}
                                                selectedCity={selectedCity}
                                                isUpcoming={isUpcoming}
                                                useSplashPopup={useSplashPopup || isPriceInCityRoute}
                                                className={`shrink-0 whitespace-nowrap rounded-lg border border-[#087F78] px-2 py-2 md:px-3 text-[11px] md:text-[13px] font-medium text-[#087F78] transition hover:bg-teal-50 cursor-pointer`}
                                                dataTestId="view-emi-offers-button"
                                            />
                                        )
                                    }
                                />
                            )}

                            <div ref={offerBtnRef} className="w-full">
                                <CommonLeadButton
                                    GORPFormHeading={GORPFormHeading}
                                    isPriceInCityRoute={isPriceInCityRoute}
                                    leadCTAText={leadCTAText}
                                    title={`${carName} Offers`}
                                    data={data}
                                    selectedCity={selectedCity}
                                    isUpcoming={isUpcoming}
                                    useSplashPopup={useSplashPopup}
                                    className={`h-12 w-full rounded-xl bg-[#087F78] font-medium text-white transition hover:bg-teal-800 cursor-pointer ${gorpTrackingClass}`}
                                />
                            </div>
                        </div>
                    )}

                    {/* Action Buttons */}
                    {isDiscontinued ? (
                        <>
                            {latestModel && latestModel?.slug !== data?.slug && (
                                <div className="mt-4 md:mt-6">
                                    <div className="bg-white border border-gray-200 rounded-[16px] md:rounded-[12px] overflow-hidden flex flex-col md:flex-row w-full">
                                        <div className="md:hidden bg-[#087F78] py-1.5 px-4">
                                            <p className="text-white text-[10px] font-bold text-center uppercase tracking-wider">Latest Launch by {latestModel?.brand_name} ✨</p>
                                        </div>

                                        {/* Desktop Image */}
                                        <div className="hidden md:block w-[140px] shrink-0 relative">
                                            <ImageWrapper
                                                src={latestModel?.featured_image}
                                                alt={latestModel?.name}
                                                fill
                                                sizes="140px"
                                                sourceWidth={140}
                                                sourceHeight={93}
                                                className="object-cover"
                                            />
                                        </div>

                                        <div className="flex-1 flex flex-col">
                                            {/* Desktop Header */}
                                            <div className="hidden md:block bg-[#087F78] py-1.5 px-4">
                                                <p className="text-white text-[11px] font-[400] uppercase tracking-wider">Latest Launch by {latestModel?.brand_name} ✨</p>
                                            </div>

                                            {/* Content Area */}
                                            <div className="flex flex-row md:flex-col justify-between items-center md:items-start p-2 bg-[#F9F9F9] md:bg-white">
                                                <div>
                                                    <h4 className="text-[16px] font-bold text-[#0E1116]">
                                                        {" "}
                                                        {latestModel?.brand_name} {latestModel?.name}
                                                    </h4>
                                                    <p className="text-[12px] font-bold text-[#2A2F38]">{formatPriceRange(latestModel?.price_range)}</p>
                                                </div>
                                                {/* Mobile Image */}
                                                <div className="md:hidden w-[90px] h-[60px] rounded-lg overflow-hidden relative">
                                                    <ImageWrapper
                                                        src={latestModel?.featured_image}
                                                        alt={latestModel?.name}
                                                        fill
                                                        sizes="90px"
                                                        sourceWidth={90}
                                                        sourceHeight={60}
                                                        className="object-cover"
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Button */}
                                        <div className="border-t md:border-t-0 md:border-l border-gray-100 flex items-center justify-center p-3 md:px-6">
                                            <Link href={`/${latestModel?.brand_slug}/${latestModel?.slug}`} className="flex items-center gap-2">
                                                <span className="text-[13px] font-[500] text-[#0E1116]">View Details</span>
                                                <div className="bg-[#0E1116] w-[22px] h-[22px] rounded-full flex items-center justify-center">
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                                                        <path d="M5 12h14M12 5l7 7-7 7" />
                                                    </svg>
                                                </div>
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            {/* Mobile Fixed Button */}
                            {showStickyBtn && (
                                <div className="fixed bottom-0 left-0 right-0 z-50 border-t border-gray-200 bg-white p-4 shadow-lg md:hidden">
                                    <CommonLeadButton
                                        GORPFormHeading={GORPFormHeading}
                                        isPriceInCityRoute={isPriceInCityRoute}
                                        leadCTAText={leadCTAText}
                                        data={data}
                                        selectedCity={selectedCity}
                                        isUpcoming={isUpcoming}
                                        useSplashPopup={useSplashPopup}
                                        className={`h-12 w-full rounded-xl bg-[#087F78] font-medium text-white`}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* EMI Calculator modal */}
            {isEmiOpen && (
                <EmiCalculator
                    carName={carName}
                    variants={variants}
                    onRoadPrices={
                        Object.keys(emiOnRoadPrices).length > 0 || (selectedVariant?._id && rtoResult && insuranceResult)
                            ? {
                                  ...emiOnRoadPrices,
                                  ...(selectedVariant?._id && rtoResult && insuranceResult
                                      ? {
                                            [selectedVariant._id]: (selectedVariant?.price || price) + rtoResult.total + insuranceResult.total,
                                        }
                                      : {}),
                              }
                            : undefined
                    }
                    cityName={selectedCity}
                    isEmiRoute={false}
                    isMobile={isMobile}
                    selectedVariantId={emiSelectedVariant?._id}
                    onVariantChange={(nextVariant) => setEmiSelectedVariant(nextVariant)}
                    isOpen={isEmiOpen}
                    onClose={() => setIsEmiOpen(false)}
                    VarentSectorClass={PDP_EMICALCULATOR_POPUP_TRACKING.PDP_VARIANT_SELECTOR_CLICK}
                    DownPaymentClass={PDP_EMICALCULATOR_POPUP_TRACKING.PDP_DOWN_PAYMENT_CLICK}
                    LoanTermClass={PDP_EMICALCULATOR_POPUP_TRACKING.PDP_LOAN_PERIOD_CLICK}
                    InterestRateClass={PDP_EMICALCULATOR_POPUP_TRACKING.PDP_INTEREST_RATE_CLICK}
                />
            )}
        </div>
    );
}

// common lead button for hero section and sticky section
const CommonLeadButton = ({
    GORPFormHeading,
    isPriceInCityRoute,
    leadCTAText,
    title,
    data,
    selectedCity,
    isUpcoming,
    useSplashPopup,
    className,
    dataTestId,
}: {
    GORPFormHeading?: React.ReactNode;
    isPriceInCityRoute?: boolean;
    leadCTAText?: string;
    title?: string;
    data?: any;
    selectedCity?: string;
    isUpcoming?: boolean;
    useSplashPopup?: boolean;
    className?: string;
    dataTestId?: string;
}) => {
    return (
        <LeadButton
            title={title}
            {...(GORPFormHeading
                ? { formHeading: GORPFormHeading }
                : isPriceInCityRoute
                  ? {
                        formHeading: (
                            <>
                                view on road price for{" "}
                                <span className="font-semibold text-[#008C8B]">
                                    {data?.brand_name} {data?.name}
                                </span>{" "}
                                in <span className="font-semibold text-[#008C8B]">{selectedCity}</span>
                            </>
                        ),
                    }
                  : {})}
            btnText={
                leadCTAText ? (
                    leadCTAText
                ) : isUpcoming ? (
                    <div className="w-full flex justify-center items-center gap-3">
                        Notify Me <Image src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}well-icon.svg`} alt="Share" title="well icon" width={20} height={20} className="h-[20px] w-[20px]" />
                    </div>
                ) : isPriceInCityRoute ? (
                    "Get Offers"
                ) : (
                    // `View ${new Date().toLocaleString("en-IN", { month: "long" })} Offers`
                    `View Diwali Offers`
                )
            }
            carId={data?._id}
            variantId={data?.variant_id}
            carName={data?.name}
            useSplashPopup={useSplashPopup || isPriceInCityRoute}
            className={`${className}`}
            is_upcoming={isUpcoming}
            datatestid={dataTestId}
        />
    );
};
